import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import AgentDetailPanel from '../../components/admin/AgentDetailPanel';
import AgentFormPanel from '../../components/admin/AgentFormPanel';
import AlertMessage from '../../components/shared/alertMessage';
import {
  setAgentsData,
  addAgent,
  updateAgent,
  deleteAgent,
  setLoading,
  setError,
  clearError,
} from '../../components/redux/slices/voiceAIAgentsSlice';

const VoiceAIAgentsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { agentId } = useParams();

  const agentsData = useSelector((state) => state.voiceAIAgents.agentsData);
  const loading = useSelector((state) => state.voiceAIAgents.loading);
  const error = useSelector((state) => state.voiceAIAgents.error);

  const [showDetailsSidebar, setShowDetailsSidebar] = useState(false);
  const [showFormSidebar, setShowFormSidebar] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [healthSystems, setHealthSystems] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  // Handle URL-based routing
  useEffect(() => {
    const path = location.pathname;
    const isCreate = path.endsWith('/create');
    const isEdit = path.endsWith('/edit') || path.includes('/edit/');

    if (isCreate) {
      setEditingAgent(null);
      setShowFormSidebar(true);
      setShowDetailsSidebar(false);
    } else if (isEdit && agentId && agentsData.length > 0) {
      const agent = agentsData.find(a => a.id === agentId);
      if (agent) {
        setEditingAgent(agent);
        setShowFormSidebar(true);
        setShowDetailsSidebar(false);
      }
    } else if (agentId && agentsData.length > 0) {
      const agent = agentsData.find(a => a.id === agentId);
      if (agent) {
        setSelectedAgent(agent);
        setShowDetailsSidebar(true);
        setShowFormSidebar(false);
      }
    } else {
      setSelectedAgent(null);
      setEditingAgent(null);
      setShowDetailsSidebar(false);
      setShowFormSidebar(false);
    }
  }, [agentId, agentsData, location.pathname]);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(clearError());

    try {
      const response = await adminApi.getVoiceAIAgents();

      if (response.data) {
        dispatch(setAgentsData(response.data));
      }
    } catch (err) {
      dispatch(setError(err.message || 'Failed to fetch agents'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Fetch health systems and organizations once on mount
  useEffect(() => {
    const fetchHealthSystemsAndOrgs = async () => {
      try {
        const [hsResponse, orgResponse] = await Promise.all([
          adminApi.getHealthSystems().catch(() => ({ data: { health_systems: [] } })),
          adminApi.getOrganizations().catch(() => ({ data: { organizations: [] } })),
        ]);
        setHealthSystems(Array.isArray(hsResponse?.data?.health_systems) ? hsResponse.data.health_systems : []);
        setOrganizations(Array.isArray(orgResponse?.data?.organizations) ? orgResponse.data.organizations : []);
      } catch (error) {
        setHealthSystems([]);
        setOrganizations([]);
      }
    };
    fetchHealthSystemsAndOrgs();
  }, []);

  // Navigation handlers
  const handleCloseSidebar = () => {
    navigate('/admin/voiceai-agents');
  };

  const handleAdd = () => {
    navigate('/admin/voiceai-agents/create');
  };

  const handleEdit = (agent) => {
    navigate(`/admin/voiceai-agents/${agent.id}/edit`);
  };

  // Actions Cell Renderer
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    return (
      <div className="d-flex align-items-center gap-1" style={{ height: '100%' }}>
        {/* <button
          className="btn btn-info btn-sm"
          onClick={() => handleSelectAgent(data)}
          title="View Agent"
        ><i className="fa fa-eye"></i>
        </button> */}
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleEdit(data)}
          title="Edit Agent"
        ><i className="fa fa-pencil"></i>
        </button>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => handleDelete(data)}
          title="Delete Agent"
        ><i className="far fa-trash-can"></i>
        </button>
      </div>
    );
  };

  // Status Badge Renderer
  const StatusCellRenderer = (props) => {
    const isActive = props.value;
    return (
      <span className={`badge bg-${isActive ? 'success' : 'secondary'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  // Column Definitions
  const columnDefs = [
    {
      headerName: 'Actions',
      maxWidth: 90,
      cellRenderer: ActionsCellRenderer,
      pinned: 'left',
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Agent Name',
      field: 'agent_name',
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: 'Agent ID',
      field: 'agent_id',
      minWidth: 200,
    },
    {
      headerName: 'Phone Number',
      field: 'phone_number',
      minWidth: 150,
    },
    {
      headerName: 'Associated With',
      minWidth: 200,
      flex: 1,
      valueGetter: (params) => {
        if (!params.data) return '';
        if (params.data.assigned_health_system_name) {
          return `HS: ${params.data.assigned_health_system_name}`;
        }
        if (params.data.assigned_organization_name) {
          return `Org: ${params.data.assigned_organization_name}`;
        }
        return '-';
      },
    },
    {
      headerName: 'Description',
      field: 'description',
      minWidth: 250,
      flex: 1,
    },
    {
      headerName: 'Status',
      field: 'is_active',
      minWidth: 100,
      cellRenderer: StatusCellRenderer,
    },
    {
      headerName: 'Created',
      field: 'created_at',
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString();
      },
    },
  ];


  const handleDelete = async (agent) => {
    if (!window.confirm(`Are you sure you want to delete agent "${agent.agent_name}"?`)) {
      return;
    }

    try {
      await adminApi.deleteVoiceAIAgent(agent.id);
      dispatch(deleteAgent(agent.id));
      setAlertState({ show: true, type: 'success', title: '', message: `Agent "${agent.agent_name}" deleted successfully` });
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Delete Failed', message: err.message || 'An issue occurred while deleting agent. Try again later.' });
    }
  };

  const handleSaveAgent = async (agentData) => {
    try {
      if (editingAgent) {
        const response = await adminApi.updateVoiceAIAgent(editingAgent.id, agentData);
        dispatch(updateAgent(response.data));
        setTimeout(() => setAlertState({ show: true, type: 'success', title: '', message: `Agent "${agentData.agent_name}" updated successfully` }), 300);
      } else {
        const response = await adminApi.createVoiceAIAgent(agentData);
        dispatch(addAgent(response.data));
        setTimeout(() => setAlertState({ show: true, type: 'success', title: '', message: `Agent "${agentData.agent_name}" created successfully` }), 300);
      }
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message || 'Failed to save agent');
    }
  };

  return (
      <>
      <div className="page-title-box d-flex align-items-center justify-content-between">
        <div className="col-8">
          <h4 className="text-lg">Voice AI Agents</h4>
          <p className="text-muted">Central resource for managing VoiceAI Agents and Organization/Healthcare relationships</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/">Admin</a></li>
              <li className="breadcrumb-item"><a href="/admin/settings/">Settings</a></li>
              <li className="breadcrumb-item active">VoiceAI Agents</li>
            </ol>
          </nav>
        </div>
      </div>

      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          dismissible={true}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => dispatch(clearError())}
          ></button>
        </div>
      )}

      <div className="card">
        {loading && agentsData.length === 0 ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading agents...</p>
          </div>
        ) : (
          <div>
            <div className="d-flex justify-content-end p-2">
              <button className="btn btn-primary btn-sm" onClick={handleAdd}>
                <i className="fa fa-plus me-2"></i>
                Add Agent
              </button>
            </div>
            <AgGridTable
              rowData={agentsData}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
              }}
            />
          </div>
        )}
      </div>

      <AgentDetailPanel
        agent={selectedAgent}
        isOpen={showDetailsSidebar}
        onClose={handleCloseSidebar}
        onEdit={() => handleEdit(selectedAgent)}
      />

      <AgentFormPanel
        agent={editingAgent}
        isOpen={showFormSidebar}
        onClose={handleCloseSidebar}
        onSave={handleSaveAgent}
        healthSystems={healthSystems}
        organizations={organizations}
      />
      </>
  );
};

export default VoiceAIAgentsPage;
